/**
 * useGovernmentDerivation — Government-group customerType derivation.
 *
 * Unlike Individual (§8 auto-derivation from DOB/citizenship), Government
 * sub-type is MANUALLY SELECTED by the officer from the 3 values documented
 * in government.md §"Entity sub-types":
 *
 *   - PublicFunds (primary) → customerType `'PublicFund'` (shared-types enum)
 *                             callClass=22, tinCode=E, cddExempt=true reason 15
 *   - Donation              → customerType `'Other'` (no canonical enum value;
 *                             finer variant in extra.donationVariant)
 *                             callClass=32, tinCode varies by variant
 *   - ICS_Personal          → customerType `'Other'`
 *                             callClass=14, tinCode=E
 *
 * The finer sub-type is stored in `person.extra.governmentSubType` so
 * downstream Core Director mapping can key off it. Wave-3 promotion: add
 * the specific values to the shared `CustomerType` enum.
 *
 * Existing-CIF reload behaviour: since sub-type is officer-selected and not
 * auto-derived, we don't auto-promote on reload. The stored sub-type is
 * hydrated from extra and shown as-is.
 *
 * TODO(bill): government walkthrough needed — finalize canonical customerType
 * mapping per sub-type. See government.md §Open Questions #3 (split Donation
 * into DonationShortTerm/DonationLongTerm enum values?).
 */

import { useMemo } from 'react';
import type { CustomerType } from '@retail-ops/shared-types/enums';
import type { RaoPerson } from '../../../types';
import type {
  GovernmentSubType,
  DonationVariant,
} from './government-config';
import {
  GOVERNMENT_SUB_TYPES,
  DONATION_VARIANTS,
  SUPERVISOR_APPROVAL_SUBTYPES,
} from './government-config';

// -----------------------------------------------------------------------------
// Public API
// -----------------------------------------------------------------------------

export interface DerivationResult {
  /** Canonical CustomerType written to person.customerType. */
  readonly customerType: CustomerType;
  /** Finer Government sub-type — stored in extra.governmentSubType. */
  readonly governmentSubType: GovernmentSubType;
  /** Donation sub-variant (only relevant when sub-type === 'Donation'). */
  readonly donationVariant?: DonationVariant;
  /** Core Director call class (Field 23). */
  readonly callClass: string;
  /** TIN code default (S/E/N). */
  readonly tinCode: 'S' | 'E' | 'N';
  /** Consumer flag (Field 39). */
  readonly consumerFlag: boolean;
  /** CDD-exempt flag (Field 371). */
  readonly cddExempt: boolean;
  /** CDD exempt reason code (Field 378). */
  readonly cddExemptReasonCode?: string;
  /** True when sub-type is Finance-only (dialog renders a blocking banner). */
  readonly financeOnly: boolean;
  /** True when Finance-approval gate is required. */
  readonly requiresFinanceApproval: boolean;
  /** True when Manager/Executive approval is required for CIF commit. */
  readonly requiresSupervisorApproval: boolean;
  /** Origin signal — 'baseline' (default PublicFunds) | 'officer' | 'stored'. */
  readonly derivedFrom: 'baseline' | 'officer' | 'stored';
}

export interface UseGovernmentDerivationOptions {
  readonly mode: 'new' | 'existing';
  readonly storedType?: string;
}

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

/**
 * Map a Government sub-type to the canonical CustomerType enum value. The
 * shared-types enum only has 'PublicFund' today; Donation + ICS_Personal
 * fall back to 'Other' until Wave-3 enum expansion lands.
 *
 * TODO(bill): government walkthrough needed — confirm canonical mapping.
 */
function mapSubTypeToCustomerType(sub: GovernmentSubType): CustomerType {
  switch (sub) {
    case 'PublicFunds':
      return 'PublicFund';
    case 'Donation':
    case 'ICS_Personal':
      return 'Other';
    default:
      return 'Other';
  }
}

function readStoredSubType(person: RaoPerson): GovernmentSubType | undefined {
  const extra = (person.extra ?? {}) as Record<string, unknown>;
  const v = extra['governmentSubType'];
  if (typeof v === 'string' && GOVERNMENT_SUB_TYPES.some((s) => s.value === v)) {
    return v as GovernmentSubType;
  }
  return undefined;
}

function readStoredDonationVariant(person: RaoPerson): DonationVariant | undefined {
  const extra = (person.extra ?? {}) as Record<string, unknown>;
  const v = extra['donationVariant'];
  if (typeof v === 'string' && DONATION_VARIANTS.some((d) => d.value === v)) {
    return v as DonationVariant;
  }
  return undefined;
}

// -----------------------------------------------------------------------------
// Hook
// -----------------------------------------------------------------------------

/**
 * Compute the derived Government entity configuration from the current
 * Person + officer's sub-type selection.
 *
 * Memoized on extra.governmentSubType + extra.donationVariant — the two
 * signals that drive the result. Unrelated field edits won't thrash.
 */
export function useGovernmentDerivation(
  person: RaoPerson,
  opts: UseGovernmentDerivationOptions,
): DerivationResult {
  const storedSubType = readStoredSubType(person);
  const storedVariant = readStoredDonationVariant(person);

  return useMemo<DerivationResult>(() => {
    // Sub-type selection — default to PublicFunds for new persons (primary
    // path per government.md §16 walkthrough priority).
    const subType: GovernmentSubType = storedSubType ?? 'PublicFunds';
    const derivedFrom: DerivationResult['derivedFrom'] =
      storedSubType
        ? (opts.mode === 'existing' ? 'stored' : 'officer')
        : 'baseline';

    const option = GOVERNMENT_SUB_TYPES.find((s) => s.value === subType)
      ?? GOVERNMENT_SUB_TYPES[0];

    // Donation variant resolution — default ShortTerm when sub-type=Donation.
    let donationVariant: DonationVariant | undefined;
    let tinCode: 'S' | 'E' | 'N' = option.tinCodeDefault;
    let callClass: string = option.callClass;
    if (subType === 'Donation') {
      donationVariant = storedVariant ?? 'ShortTerm';
      const variantOption = DONATION_VARIANTS.find((d) => d.value === donationVariant);
      if (variantOption) {
        tinCode = variantOption.tinCode;
        callClass = variantOption.callClass;
      }
    }

    const customerType = mapSubTypeToCustomerType(subType);

    const requiresSupervisorApproval = SUPERVISOR_APPROVAL_SUBTYPES.has(subType);

    return {
      customerType,
      governmentSubType: subType,
      donationVariant,
      callClass,
      tinCode,
      consumerFlag: option.consumerFlag,
      cddExempt: option.cddAutoExempt,
      cddExemptReasonCode: option.cddExemptReasonCode,
      financeOnly: option.financeOnly,
      requiresFinanceApproval: option.requiresFinanceApproval,
      requiresSupervisorApproval,
      derivedFrom,
    };
  }, [storedSubType, storedVariant, opts.mode]);
}
